import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { FiActivity, FiBarChart2, FiCheckCircle, FiClock, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';
import '../styles/admin-common.css';
import './AnalyticsDashboardPage.css';
import { chartTheme } from '../styles/chartTheme';
import { formatExamCode } from '../utils/format';

interface AnalyticsData {
  userMetrics: {
    totalUsers: number;
    activeUsers: { daily: number; weekly: number; monthly: number };
    newUsers: { today: number; thisWeek: number; thisMonth: number };
    usersByPlan: { free: number; basic: number; premium: number };
  };
  examMetrics: {
    totalExams: number;
    completedExams: number;
    averageScore: number;
    completionRate: number;
    passRate: number;
    scoreDistribution: { range: string; count: number }[];
    examsByType: Record<string, number>;
  };
  systemMetrics: {
    activeExamSessions: number;
    totalQuestions: number;
    systemUptime: number;
  };
  contentMetrics: {
    popularExamTypes: { examType: string; attempts: number; averageScore: number }[];
  };
}


const AnalyticsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'exams'>('overview');

  const apiCall = async (url: string) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const result = await apiCall('/admin/analytics/overview');
      if (result.success) {
        setAnalyticsData(result.data);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    return `${days}일 ${hours}시간`;
  };

  const safeRatio = (value: number, total: number) => {
    if (!total) return 0;
    return Math.round((value / total) * 1000) / 10;
  };

  const usersByPlanSeries = useMemo(() => {
    if (!analyticsData) return [0, 0, 0];
    return [
      analyticsData.userMetrics.usersByPlan.free,
      analyticsData.userMetrics.usersByPlan.basic,
      analyticsData.userMetrics.usersByPlan.premium,
    ];
  }, [analyticsData]);

  const usersByPlanOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'donut', toolbar: { show: false } },
      labels: ['Free', 'Basic', 'Premium'],
      colors: [chartTheme.categorical[0], chartTheme.categorical[1], chartTheme.categorical[2]],
      legend: { position: 'bottom', fontSize: '12px' },
      dataLabels: { enabled: true },
      stroke: { colors: [chartTheme.surface], width: 2 },
      responsive: [{ breakpoint: 640, options: { chart: { height: 260 } } }],
    }),
    []
  );

  const activeUsersOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 6, columnWidth: '48%' } },
      dataLabels: { enabled: false },
      colors: [chartTheme.categorical[0], chartTheme.categorical[1]],
      xaxis: { categories: ['일간', '주간', '월간'] },
      yaxis: { labels: { formatter: (val) => `${Math.round(val)}` } },
      legend: { position: 'top' },
      grid: { borderColor: chartTheme.track },
    }),
    []
  );

  const scoreDistributionOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 6, horizontal: true, barHeight: '54%' } },
      dataLabels: { enabled: false },
      colors: [chartTheme.primary],
      xaxis: { labels: { formatter: (val) => `${Math.round(Number(val))}` } },
      grid: { borderColor: chartTheme.track },
    }),
    []
  );

  const examTypeOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: 'end', distributed: false } },
      dataLabels: { enabled: false },
      colors: [chartTheme.primary],
      legend: { show: false },
      xaxis: { labels: { rotate: -20 } },
      grid: { borderColor: chartTheme.track },
    }),
    []
  );

  const completionGaugeOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'radialBar', sparkline: { enabled: true } },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          hollow: { size: '64%' },
          dataLabels: {
            name: { show: true, offsetY: 18, color: chartTheme.muted, fontSize: '12px', fontWeight: 600 },
            value: {
              show: true,
              offsetY: -14,
              color: chartTheme.ink,
              fontSize: '28px',
              fontWeight: 800,
              formatter: (value) => `${Math.round(value)}%`,
            },
          },
        },
      },
      fill: { type: 'solid' },
      stroke: { lineCap: 'round' },
      labels: ['완료율'],
      colors: [chartTheme.primary],
    }),
    []
  );

  if (!user?.is_admin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  if (loading || !analyticsData) {
    return (
      <div className="admin-page analytics-v2">
        <div className="loading">분석 데이터를 불러오는 중...</div>
      </div>
    );
  }

  const examTypeEntries = Object.entries(analyticsData.examMetrics.examsByType || {});
  const scoreRanges = analyticsData.examMetrics.scoreDistribution || [];
  const totalUsers = analyticsData.userMetrics.totalUsers || 0;

  const activeUserSeries = [
    {
      name: '활성 사용자',
      data: [
        analyticsData.userMetrics.activeUsers.daily,
        analyticsData.userMetrics.activeUsers.weekly,
        analyticsData.userMetrics.activeUsers.monthly,
      ],
    },
    {
      name: '신규 사용자',
      data: [
        analyticsData.userMetrics.newUsers.today,
        analyticsData.userMetrics.newUsers.thisWeek,
        analyticsData.userMetrics.newUsers.thisMonth,
      ],
    },
  ];

  const scoreSeries = [
    {
      name: '응시자 수',
      data: scoreRanges.map((item) => item.count),
    },
  ];

  const scoreRangeOptions = {
    ...scoreDistributionOptions,
    xaxis: {
      ...scoreDistributionOptions.xaxis,
      categories: scoreRanges.map((item) => `${item.range}점`),
    },
  };

  const examTypeSeries = [
    {
      name: '응시 수',
      data: examTypeEntries.map(([, count]) => count),
    },
  ];

  const examTypeChartOptions = {
    ...examTypeOptions,
    xaxis: {
      ...examTypeOptions.xaxis,
      categories: examTypeEntries.map(([type]) => formatExamCode(type)),
    },
  };

  return (
    <div className="admin-page analytics-v2">
      <div className="admin-header analytics-v2__header">
        <div>
          <h1>운영 분석</h1>
        </div>
        <button className="admin-btn secondary" onClick={loadAnalytics}>
          <FiRefreshCw /> 새로고침
        </button>
      </div>

      <div className="analytics-v2__tabs">
        <button className={`analytics-v2__tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          개요
        </button>
        <button className={`analytics-v2__tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          사용자
        </button>
        <button className={`analytics-v2__tab ${activeTab === 'exams' ? 'active' : ''}`} onClick={() => setActiveTab('exams')}>
          시험
        </button>
      </div>

      <section className="analytics-v2__kpi-grid">
        <article className="analytics-v2__kpi-card">
          <span className="analytics-v2__kpi-icon blue"><FiUsers /></span>
          <div>
            <p>총 사용자</p>
            <h3>{analyticsData.userMetrics.totalUsers.toLocaleString()}</h3>
            <small>이번 달 +{analyticsData.userMetrics.newUsers.thisMonth}</small>
          </div>
        </article>
        <article className="analytics-v2__kpi-card">
          <span className="analytics-v2__kpi-icon green"><FiCheckCircle /></span>
          <div>
            <p>완료 시험</p>
            <h3>{analyticsData.examMetrics.completedExams.toLocaleString()}</h3>
            <small>완료율 {analyticsData.examMetrics.completionRate.toFixed(1)}%</small>
          </div>
        </article>
        <article className="analytics-v2__kpi-card">
          <span className="analytics-v2__kpi-icon amber"><FiBarChart2 /></span>
          <div>
            <p>평균 점수</p>
            <h3>{analyticsData.examMetrics.averageScore.toFixed(1)}%</h3>
            <small>합격률 {analyticsData.examMetrics.passRate.toFixed(1)}%</small>
          </div>
        </article>
        <article className="analytics-v2__kpi-card">
          <span className="analytics-v2__kpi-icon violet"><FiActivity /></span>
          <div>
            <p>활성 시험 세션</p>
            <h3>{analyticsData.systemMetrics.activeExamSessions}</h3>
            <small>실시간 모니터링</small>
          </div>
        </article>
      </section>

      {activeTab === 'overview' && (
        <div className="analytics-v2__grid analytics-v2__grid--overview">
          <section className="analytics-v2__panel">
            <header>
              <h3>시험 유형별 응시 현황</h3>
              <span>총 {analyticsData.examMetrics.totalExams.toLocaleString()}건</span>
            </header>
            <div className="analytics-v2__chart-wrap">
              {examTypeEntries.length > 0 ? (
                <ReactApexChart type="bar" height={320} options={examTypeChartOptions} series={examTypeSeries} />
              ) : (
                <div className="analytics-v2__empty">시험 유형 데이터가 없습니다.</div>
              )}
            </div>
          </section>

          <section className="analytics-v2__panel">
            <header>
              <h3>운영 핵심 지표</h3>
              <span>시스템/콘텐츠 현황</span>
            </header>
            <div className="analytics-v2__system-list">
              <div><FiClock /><span>시스템 가동 시간</span><strong>{formatUptime(analyticsData.systemMetrics.systemUptime)}</strong></div>
              <div><FiBarChart2 /><span>총 문항 수</span><strong>{analyticsData.systemMetrics.totalQuestions.toLocaleString()}개</strong></div>
              <div><FiActivity /><span>활성 세션</span><strong>{analyticsData.systemMetrics.activeExamSessions}개</strong></div>
            </div>
          </section>

          <section className="analytics-v2__panel analytics-v2__panel--full">
            <header>
              <h3>인기 시험 유형</h3>
              <span>응시수 기준</span>
            </header>
            <div className="analytics-v2__table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>시험 유형</th>
                    <th>응시 수</th>
                    <th>평균 점수</th>
                    <th>비중</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsData.contentMetrics.popularExamTypes.map((item) => (
                    <tr key={item.examType}>
                      <td>{formatExamCode(item.examType)}</td>
                      <td>{item.attempts.toLocaleString()}</td>
                      <td>{item.averageScore.toFixed(1)}%</td>
                      <td>{safeRatio(item.attempts, analyticsData.examMetrics.totalExams)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="analytics-v2__grid">
          <section className="analytics-v2__panel">
            <header>
              <h3>요금제 분포</h3>
              <span>총 {totalUsers.toLocaleString()}명</span>
            </header>
            <div className="analytics-v2__chart-wrap">
              <ReactApexChart type="donut" height={320} options={usersByPlanOptions} series={usersByPlanSeries} />
            </div>
            <div className="analytics-v2__legend-metrics">
              <span>Free {safeRatio(analyticsData.userMetrics.usersByPlan.free, totalUsers)}%</span>
              <span>Basic {safeRatio(analyticsData.userMetrics.usersByPlan.basic, totalUsers)}%</span>
              <span>Premium {safeRatio(analyticsData.userMetrics.usersByPlan.premium, totalUsers)}%</span>
            </div>
          </section>

          <section className="analytics-v2__panel">
            <header>
              <h3>활성/신규 사용자 추이</h3>
              <span>일/주/월 비교</span>
            </header>
            <div className="analytics-v2__chart-wrap">
              <ReactApexChart type="bar" height={320} options={activeUsersOptions} series={activeUserSeries} />
            </div>
          </section>
        </div>
      )}

      {activeTab === 'exams' && (
        <div className="analytics-v2__grid">
          <section className="analytics-v2__panel">
            <header>
              <h3>점수 구간 분포</h3>
              <span>총 {analyticsData.examMetrics.completedExams.toLocaleString()}건</span>
            </header>
            <div className="analytics-v2__chart-wrap">
              <ReactApexChart type="bar" height={320} options={scoreRangeOptions} series={scoreSeries} />
            </div>
          </section>

          <section className="analytics-v2__panel analytics-v2__panel--gauge">
            <header>
              <h3>시험 완료율</h3>
              <span>전체 대비 완료</span>
            </header>
            <div className="analytics-v2__gauge">
              <ReactApexChart
                type="radialBar"
                height={300}
                options={completionGaugeOptions}
                series={[analyticsData.examMetrics.completionRate]}
              />
            </div>
            <ul className="analytics-v2__bullet-stats">
              <li>총 시험 시작: {analyticsData.examMetrics.totalExams.toLocaleString()}건</li>
              <li>완료 시험: {analyticsData.examMetrics.completedExams.toLocaleString()}건</li>
              <li>합격률: {analyticsData.examMetrics.passRate.toFixed(1)}%</li>
            </ul>
          </section>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboardPage;
